import { useCallback, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../../api/adminApi";
import type { FundDetails } from "../../../../../api/types/fundStructureTypes";
import type { FieldIdToValueMap, ObjectClassDefinition, ObjectPermissions } from "../../../../../api/types/objectTypes";
import useClientContext from "../../../../../context/useClientContext";
import ScrollableColumn from "../../../../common/layout/ScrollableColumn";
import ScrollableColumns from "../../../../common/layout/ScrollableColumns";
import CreatedAndModifiedTimestamps from "../../../../entityFields/CreatedAndModifiedTimestamps";
import EntityNotesSection from "../../../../entityFields/EntityNotesSection";
import FieldValuesSection from "../../../../entityFields/FieldValuesSection";
import { getNotesEntityType } from "../../../../entityFields/entityTypesHelper";
import { NotesContextProvider } from "../../../../notes/NotesContextProvider";

interface Props {
  fundDetails: FundDetails;
  objectDefinition: ObjectClassDefinition;
  objectPermissions: ObjectPermissions;
  onFundDetailsChange: (details: Partial<FundDetails>) => void;
}

const updateFieldValues = withErrorHandling(adminApi.updateFundFieldValues);

const FundMainInfoTab = ({ fundDetails, objectDefinition, objectPermissions, onFundDetailsChange }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const { hasAnyPermission } = useClientContext();

  const [renderKey, setRenderKey] = useState(0);

  const getFieldValues = useCallback(() => adminApi.getFundFieldValues(fundDetails.id), [fundDetails.id]);

  const [fieldValuesResp, fieldValuesError, { setData: setFieldValues, isFetching: isFetchingFieldValues }] = useFetch(
    getFieldValues,
    { onSetData: () => setRenderKey((prev) => prev + 1) }
  );

  const getCardLayout = useCallback(
    () => adminApi.getCardLayout(objectDefinition.objectType),
    [objectDefinition.objectType]
  );

  const [cardLayoutResp, cardLayoutError, { isFetching: isFetchingCardLayout }] = useFetch(getCardLayout, {
    onSetData: () => setRenderKey((prev) => prev + 1),
  });

  const hasPermissionsToViewNotes = hasAnyPermission(objectPermissions.notesReadPermissions);
  const hasPermissionsToManageNotes = hasAnyPermission(objectPermissions.notesWritePermissions);
  const hasPermissionsToManageFields = hasAnyPermission(objectPermissions.objectDataWritePermissions);

  const saveFieldValues = async (fieldValues: FieldIdToValueMap) => {
    const values = Object.entries(fieldValues).map(([fieldId, value]) => ({ fieldId, value }));
    const [resp, error] = await updateFieldValues(fundDetails.id, { values });
    if (error) {
      sendNotificationError(error.message);
      logError(error, `[FundDetailsMainTab] saveFieldValues`);
      return false;
    }

    sendNotification("Changes saved");
    setFieldValues(resp);
    setRenderKey((prev) => prev + 1);

    const nameFieldValue = resp.values.find(({ fieldId }) => fieldId.toLowerCase() === "name");
    const name = nameFieldValue?.value?.toString() || fundDetails.name;
    onFundDetailsChange({ name, updateAt: new Date().toISOString() });

    return true;
  };

  if (fieldValuesError || cardLayoutError) {
    logError(fieldValuesError || cardLayoutError, "[FundDetailsMainTab]");
    return <DataLoadingFailed title="Loading fund fields" />;
  }

  const fieldValues = fieldValuesResp
    ? Object.fromEntries(fieldValuesResp.values.map(({ fieldId, value }) => [fieldId, value]))
    : {};

  const cardLayout = cardLayoutResp ?? { objectType: objectDefinition.objectType, fieldGroups: [] };

  return (
    <ScrollableColumns gridProps={{ columnSpacing: 2.5 }}>
      <ScrollableColumn>
        <FieldValuesSection
          key={renderKey.toString()}
          objectType={objectDefinition.objectType}
          objectId={fundDetails.id}
          businessCentralType={objectDefinition.businessCentralType}
          fields={objectDefinition.fields}
          cardLayout={cardLayout}
          fieldValues={fieldValues}
          lookupObjects={fieldValuesResp?.lookupObjects}
          allowEditing={hasPermissionsToManageFields}
          saveFieldValues={saveFieldValues}
          isLoading={isFetchingFieldValues || !fieldValuesResp || isFetchingCardLayout || !cardLayoutResp}
        />

        <CreatedAndModifiedTimestamps
          stackProps={{ sx: { mt: 2.5 } }}
          createdAt={fundDetails.createdAt}
          modifiedAt={fundDetails.updateAt}
        />
      </ScrollableColumn>
      {hasPermissionsToViewNotes && (
        <ScrollableColumn>
          <NotesContextProvider
            entityType={getNotesEntityType(objectDefinition.objectType)}
            entityId={fundDetails.id}
            hasPermissionsToManageNotes={hasPermissionsToManageNotes}
          >
            <EntityNotesSection />
          </NotesContextProvider>
        </ScrollableColumn>
      )}
    </ScrollableColumns>
  );
};

export default FundMainInfoTab;
